import { useState, useEffect } from 'react';
import apiService from '../../services/apiService';
import { Plus, Trash2, AlertCircle, FileText, Star, Users, Mail } from 'lucide-react';

const TYPE_ICONS = { Note: FileText, Important: Star, Meeting: Users, Letter: Mail };
const TYPE_COLORS = {
  Note: 'bg-blue-50 text-blue-700 border-blue-200',
  Important: 'bg-amber-50 text-amber-700 border-amber-200',
  Meeting: 'bg-purple-50 text-purple-700 border-purple-200',
  Letter: 'bg-green-50 text-green-700 border-green-200',
};

export default function MemberNotes({ memberId }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ note_type: 'Note', content: '', note_date: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    apiService.getMemberNotes(memberId)
      .then(setNotes)
      .catch(() => setNotes([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [memberId]); // eslint-disable-line

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.content.trim()) return;
    setSaving(true);
    try {
      await apiService.createMemberNote(memberId, form);
      setForm({ note_type: 'Note', content: '', note_date: new Date().toISOString().slice(0, 10) });
      setShowForm(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    await apiService.deleteMemberNote(memberId, noteId);
    load();
  };

  return (
    <div className="space-y-4">
      {/* Add note button */}
      <div className="flex justify-between items-center">
        <h2 className="font-semibold text-slate-800">Notes ({notes.length})</h2>
        <button onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors">
          <Plus className="w-4 h-4" /> Add Note
        </button>
      </div>

      {/* Note form */}
      {showForm && (
        <form onSubmit={handleSave} className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
          <div className="flex gap-3">
            <select value={form.note_type} onChange={e => setForm(f => ({ ...f, note_type: e.target.value }))}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="Note">Note</option>
              <option value="Important">Important</option>
              <option value="Meeting">Meeting</option>
              <option value="Letter">Letter</option>
            </select>
            <input type="date" value={form.note_date} onChange={e => setForm(f => ({ ...f, note_date: e.target.value }))}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <textarea
            value={form.content}
            onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
            placeholder="Write your note here…"
            rows={3}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setShowForm(false)}
              className="px-3 py-1.5 text-slate-600 hover:text-slate-800 text-sm transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving || !form.content.trim()}
              className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors">
              {saving ? 'Saving…' : 'Save Note'}
            </button>
          </div>
        </form>
      )}

      {/* Notes list */}
      {loading ? (
        <div className="text-slate-400 text-sm text-center py-8">Loading notes…</div>
      ) : notes.length === 0 ? (
        <div className="text-slate-400 text-sm text-center py-8">No notes yet for this member</div>
      ) : (
        <div className="space-y-3">
          {notes.map(note => {
            const Icon = TYPE_ICONS[note.note_type] || FileText;
            const colorClass = TYPE_COLORS[note.note_type] || TYPE_COLORS.Note;
            return (
              <div key={note.id} className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium flex-shrink-0 ${colorClass}`}>
                      <Icon className="w-3 h-3" />
                      {note.note_type}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.content}</p>
                      <p className="text-xs text-slate-400 mt-1.5">
                        {new Date(note.note_date).toLocaleDateString('en-GB')}
                        {note.created_by && ` · ${note.created_by}`}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => handleDelete(note.id)}
                    className="flex-shrink-0 p-1.5 text-slate-300 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
